/*
 * File: input-quick-table.ts
 * File Created: 2024-11-11 09:13:23
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2025-12-08 17:13:00
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { PropInnateSync } from "@root/core/vue-props";
import { BaseInputComponent } from "../../core/lib";
import {
    Component,
    Emit,
    IQuickUnit,
    MetaAttribute,
    ObjectHelper,
    Prop,
    QuickHelper,
} from "@root/core";
import { BaseTableCore } from "@root/components/table/core/lib/base-table-core";
import {
    IBaseInputTableInterfaces,
    IBaseInputTableUITags,
} from "../core/base-input-table";
import { IBaseComponentUITag, IServer } from "@root/components/interfaces";
import QuickList from "@root/components/quick/quick-list/quick-list.vue";

type IInputQuickTableParent = { name: string; id?: string | null };

const defaultServer: IServer = { params: { paging: { pageSize: 3 } } };

// export interface IInputTableUITags {
//     base?: (this: IQuickUnit) => IBaseComponentUITag;
//     C?: (this: IQuickUnit) => IBaseComponentUITag;
//     R?: (this: IQuickUnit) => IBaseComponentUITag;
//     U?: (this: IQuickUnit) => IBaseComponentUITag;
//     D?: (this: IQuickUnit) => IBaseComponentUITag;
// }

@Component
export default class InputQuickTable extends BaseInputComponent {
    @PropInnateSync("value", { type: Array, default: () => [] })
    declare innateValue: Array<any>;

    @Prop({ type: Object, required: true })
    quick!: IQuickUnit;

    @Prop({ type: Object, required: true })
    quickParent!: IQuickUnit;

    @Prop({ type: Object, required: false })
    parent?: IInputQuickTableParent;

    @Prop({ type: Object, required: false })
    uiTags?: IBaseInputTableUITags;

    @Emit("added")
    doAdd(row) {
        if (!this.isFromServer) {
            this.innateValue = [...(this.innateValue ?? []), row];
        }
        return row;
    }

    @Emit("updated")
    doEdit(row, old) {
        if (!this.isFromServer) {
            const index = this.innateValue.findIndex(
                (o) => BaseTableCore.getDataId(o) === BaseTableCore.getDataId(old ?? row),
            );
            this.innateValue = [
                ...this.innateValue.slice(0, index),
                row,
                ...this.innateValue.slice(index + 1),
            ];
        }
        return row;
    }

    @Emit("deleted")
    doDelete(row) {
        if (!this.isFromServer) {
            const index = this.innateValue.findIndex(
                (o) => BaseTableCore.getDataId(o) === BaseTableCore.getDataId(row),
            );
            this.innateValue = [
                ...this.innateValue.slice(0, index),
                ...this.innateValue.slice(index + 1),
            ];
        }
        return row;
    }

    /// table got query updated
    doUpdated(row) {
        if (this.isFromServer) {
            /// simulate empty data
            this.innateValue = new Array(row.paging.total ?? 0);
        }
    }

    /// private helpers
    get isFromServer() {
        return !!this.parent?.id;
    }

    get servers() {
        /// rule:
        // ---
        // on create (no parent id)
        // - C: value add
        // - R: value
        // - U: value update
        // - D: value delete
        // - submit: with { create }
        // ---
        // on update (with parent id)
        // - C: server direct add
        // - R: query with { where }
        // - U: server direct update
        // - D: server direct delete
        // - submit: normal

        const whereParam = {
            where: this.isFromServer
                ? {
                      [this.parent!.name]: {
                          id: this.parent?.id,
                      },
                  }
                : {},
        };
        return {
            C: this.isFromServer ? QuickHelper.serverC(this.quick) : null,
            R: this.isFromServer
                ? QuickHelper.serverR(
                      this.quick,
                      null,
                      ObjectHelper.deepMerge(defaultServer.params, whereParam),
                  )
                : null,
            U: this.isFromServer ? QuickHelper.serverU(this.quick) : null,
            D: this.isFromServer ? QuickHelper.serverD(this.quick) : null,
        };
    }

    // interfaces
    get infs(): IBaseInputTableInterfaces {
        return {
            base: this.quick.infRead,
            C: this.quick.infCreate,
            R: this.quick.infRead,
            U: this.quick.infUpdate,
            D: this.quick.infDelete,
        };
    }

    get tags(): IBaseInputTableUITags {
        const { base, C, R, U, D } = this.uiTags ?? {};
        // let vbase = base?.call(this.quick);
        // let vC = C?.call(this.quick);
        // let vR = R?.call(this.quick);
        // let vU = U?.call(this.quick);
        // let vD = D?.call(this.quick);

        return ObjectHelper.deepMerge(
            {
                C: this.quick.uiTagCreate?.call(this.quick),
                R: this.quick.uiTagRead?.call(this.quick),
                U: this.quick.uiTagUpdate?.call(this.quick),
                D: this.quick.uiTagDelete?.call(this.quick),
            },
            {
                base,
                C,
                R,
                U,
                D,
            },

            // {
            //     base: vbase,
            //     C: vC ?? vbase,
            //     R: vR ?? vbase,
            //     U: vU ?? vbase,
            //     D: vD ?? vbase,
            // },
        );
    }
}
